import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import MyApiClient from "../../../../api-client/MyApiClient";

/**
 * `gestisceTessere`: solo ASD e SSD tesserano. Per una societa' di capitali il
 * campo non va mostrato, e soprattutto il suo valore non va rimandato al server:
 * se il caricamento fosse fallito, lo stato sarebbe rimasto al default 1 e salvare
 * la numerazione soci trascinerebbe un licenseNumberStart=1, che il backend
 * rifiuta con 409 perche' i numeri di partenza si possono solo alzare. Risultato:
 * una SRL non riuscirebbe piu' a salvare nemmeno la numerazione dei soci.
 */
export const FormNumerazione = ({ gestisceTessere = true }) => {

	const [memberNumberStart, setMemberNumberStart] = useState(1);
	const [licenseNumberStart, setLicenseNumberStart] = useState(1);
	const [memberNumberCurrent, setMemberNumberCurrent] = useState(null);
	const [licenseNumberCurrent, setLicenseNumberCurrent] = useState(null);

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');
	// Errore di caricamento mostrato inline nel tab, non come modal bloccante:
	// questo componente parte a ogni apertura di "La mia struttura" (react-bootstrap
	// monta tutti i tab), quindi un blip transitorio non deve interrompere l'utente.
	const [loadError, setLoadError] = useState(false);

	const getToken = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		return JSON.parse(tokenDetailsString).idToken;
	};

	const mostraMessaggio = (titolo, testo) => {
		setModalTitle(titolo);
		setModalText(testo);
		setModalShowed(true);
	};

	const applica = (dati) => {
		setMemberNumberStart(dati.memberNumberStart);
		setLicenseNumberStart(dati.licenseNumberStart);
		setMemberNumberCurrent(dati.memberNumberCurrent ?? null);
		setLicenseNumberCurrent(dati.licenseNumberCurrent ?? null);
	};

	const carica = async () => {
		const token = getToken();
		if (!token) return;

		const apiClient = new MyApiClient();
		setLoadError(false);
		setLoadingShowed(true);

		// Un retry assorbe il blip transitorio da cold-start della Lambda: il
		// secondo tentativo colpisce un'istanza ormai calda.
		let risultato = await apiClient.getClubNumbering(token);
		if (!risultato || risultato.error) {
			await new Promise(r => setTimeout(r, 800));
			risultato = await apiClient.getClubNumbering(token);
		}
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			setLoadError(true);
			return;
		}
		applica(risultato);
	};

	useEffect(() => {
		carica();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const salva = async () => {
		const token = getToken();
		if (!token) return;

		if (memberNumberStart < 1 || (gestisceTessere && licenseNumberStart < 1)) {
			mostraMessaggio("Errore durante l'operazione", "I numeri di partenza devono essere maggiori o uguali a 1.");
			return;
		}

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.updateClubNumbering(token, {
			memberNumberStart: Number(memberNumberStart),
			...(gestisceTessere && { licenseNumberStart: Number(licenseNumberStart) })
		});
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			// Il backend rifiuta con 409 se si tenta di abbassare un contatore:
			// quei numeri risultano gia' assegnati e non sono riutilizzabili.
			mostraMessaggio("Errore durante l'operazione",
				"Non è stato possibile salvare. I numeri di partenza possono essere solo aumentati: abbassarli creerebbe duplicati con i numeri già assegnati.");
			return;
		}

		applica(risultato);
		mostraMessaggio("Salvato", "Le impostazioni di numerazione sono state aggiornate.");
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64">{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div className="mb-3">
				<h4>Numerazione univoca</h4>
				<p className="text-muted">
					Imposta il numero di partenza per l'assegnazione automatica del numero univoco progressivo.
					I numeri di partenza possono essere solo aumentati.
				</p>
			</div>

			{loadError && (
				<div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
					<span>Non è stato possibile caricare le impostazioni di numerazione.</span>
					<Button className="btn btn-sm btn-primary" onClick={carica}>Riprova</Button>
				</div>
			)}

			<Row>
				<Col md={6} className="mb-4">
					<label className="form-label">Numerazione soci — numero di partenza</label>
					<Form.Control
						type="number"
						min="1"
						value={memberNumberStart}
						onChange={(e) => setMemberNumberStart(e.target.value)}
					/>
					<small className="text-muted">
						Numero da cui partire per assegnare il numero progressivo nel libro soci.
						{memberNumberCurrent ? ` Ultimo assegnato: ${memberNumberCurrent}.` : ' Nessun numero ancora assegnato.'}
					</small>
				</Col>
				{gestisceTessere && <Col md={6} className="mb-4">
					<label className="form-label">Numerazione tessere — numero di partenza</label>
					<Form.Control
						type="number"
						min="1"
						value={licenseNumberStart}
						onChange={(e) => setLicenseNumberStart(e.target.value)}
					/>
					<small className="text-muted">
						Numero da cui partire per assegnare il numero univoco delle tessere.
						{licenseNumberCurrent ? ` Ultimo assegnato: ${licenseNumberCurrent}.` : ' Nessun numero ancora assegnato.'}
					</small>
				</Col>}
			</Row>

			<div className="d-flex justify-content-end">
				<Button className="btn btn-primary btn-add" onClick={salva}>Salva</Button>
			</div>
		</>
	)
}

export default FormNumerazione;
